import React, { memo } from 'react'
import { useIntl } from 'react-intl'
import { useAccess } from 'contexts/access'
import { useOrganization } from 'contexts/organization'
import { advancedApplied } from 'components/filter/utilities'
import { compact } from 'utilities/array'
import Message from 'components/message'
import { Filter } from './s'
import ImageLogo, { TextLogo } from 'components/logo'

const InsightsFilter = memo(({ filter, setFilter, corporation = false, canFetchGroupsFilterless, ...props }) => {
    const { formatMessage } = useIntl()

    const { checkFeature } = useAccess()
    const companiesAvailable = checkFeature('company-groups')

    const {
        organization = {},
        subsidiaries = []
    } = useOrganization()

    const organizations = [
        organization ?? {},
        ...(subsidiaries ?? [])
    ]

    if(!!corporation && !subsidiaries?.length) {
        return null
    }

    const unitPermissionDisclaimer = canFetchGroupsFilterless ? null : (
        <Message
            type="info"
            message={formatMessage({
                id: 'insights_unit_permissions_filter_disclaimer',
                defaultMessage: 'Only groups in which you can manage users are available.'
            })} />
    )

    const advancedFields = compact([
        (!corporation && companiesAvailable) && {
            type: 'pickEntities',
            name: 'companies',
            label: formatMessage({
                id: 'noun_companies',
                defaultMessage: 'Companies'
            }),
            entity: { type: 'company' },
            picker: {
                query: {
                    key: ['groups'],
                    path: '/units',
                    filter: {
                        types: ['company'],
                        ...(!canFetchGroupsFilterless ? {
                            permission: 'unit:insights:manage'
                        } : null)
                    }
                }
            },
            empty: [],
            content: unitPermissionDisclaimer,
            hideWhenEmpty: true
        },
        !corporation && {
            type: 'pickEntities',
            name: 'teams',
            label: formatMessage({
                id: 'noun_teams',
                defaultMessage: 'Teams'
            }),
            entity: { type: 'team' },
            picker: {
                query: {
                    key: ['groups'],
                    path: '/units',
                    filter: {
                        types: ['team'],
                        ...(!canFetchGroupsFilterless ? {
                            permission: 'unit:insights:manage'
                        } : null)
                    }
                }
            },
            empty: [],
            content: unitPermissionDisclaimer,
            hideWhenEmpty: true
        },
        !corporation && {
            type: 'pickEntities',
            name: 'locations',
            label: formatMessage({
                id: 'noun_locations',
                defaultMessage: 'Locations'
            }),
            entity: { type: 'location' },
            picker: {
                query: {
                    key: ['groups'],
                    path: '/units',
                    filter: {
                        types: ['location'],
                        ...(!canFetchGroupsFilterless ? {
                            permission: 'unit:insights:manage'
                        } : null)
                    }
                }
            },
            empty: [],
            content: unitPermissionDisclaimer,
            hideWhenEmpty: true
        },
        !!corporation && {
            type: 'checkboxes',
            name: 'organizationIds',
            label: formatMessage({
                id: 'noun_organizations',
                defaultMessage: 'Organizations'
            }),
            field: {
                options: organizations.map(({ id, name, companyLogo }) => ({
                    id,
                    name: (
                        <>
                            {!!companyLogo && (
                                <ImageLogo
                                    logo={companyLogo}
                                    className="small"
                                    as="span" />
                            )}
                            {!companyLogo && (
                                <TextLogo
                                    title={name}
                                    className="small"
                                    textLength={name?.length}
                                    as="span">
                                    {name}
                                </TextLogo>
                            )}
                        </>
                    )
                }))
            },
            empty: []
        }
    ])

    return (
        <Filter
            {...props}
            filter={filter}
            setFilter={setFilter}
            advancedFields={advancedFields}
            items={['🤠']}
            fetching={false}
            hasFetched={true}
            salt="insights" />
    )
})

const advancedNames = ['companies', 'teams', 'locations', 'organizationIds']

export const areAdvancedFiltersApplied = advancedApplied(advancedNames)
export const areFiltersApplied = filter => areAdvancedFiltersApplied(filter)

export default InsightsFilter